(() => {
  if (!window.customElements || customElements.get('collection-seo-text')) return;

  class CollectionSeoText extends HTMLElement {
    connectedCallback() {
      this.onReady = () => this.initialize();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.content = this.querySelector('[data-collection-seo-content]');
      this.button = this.querySelector('[data-collection-seo-toggle]');
      if (!this.content || !this.button) {
        this.setAttribute('data-unavailable', '');
        return;
      }

      this.initialized = true;
      this.expanded = false;
      this.removeAttribute('data-unavailable');
      this.removeAttribute('data-expanded');
      this.classList.remove('is-animating');
      this.button.setAttribute('aria-expanded', 'false');
      this.button.textContent = this.button.dataset.expandLabel;
      this.focusable = new Map();
      this.onToggle = () => this.toggle();
      this.onResize = () => this.scheduleUpdate();
      this.onTransition = event => {
        if (event.target === this && event.propertyName === 'height') this.finishTransition();
      };
      this.button.addEventListener('click', this.onToggle);
      this.content.addEventListener('load', this.onResize, true);
      this.addEventListener('transitionend', this.onTransition);
      window.addEventListener('resize', this.onResize);
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(this.onResize);
        this.observer.observe(this.content);
      }
      document.fonts?.ready.then(() => {
        if (this.isConnected) this.scheduleUpdate();
      });
      this.update();
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.button?.removeEventListener('click', this.onToggle);
      this.content?.removeEventListener('load', this.onResize, true);
      this.removeEventListener('transitionend', this.onTransition);
      window.removeEventListener('resize', this.onResize);
      this.observer?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      cancelAnimationFrame(this.animationFrame);
      clearTimeout(this.transitionTimer);
      this.restoreFocus();
      this.initialized = false;
    }

    scheduleUpdate() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => this.update());
    }

    update() {
      const styles = getComputedStyle(this);
      this.collapsedHeight = parseFloat(styles.getPropertyValue('--collection-seo-height'));
      this.controlsSpace = parseFloat(styles.getPropertyValue('--collection-seo-controls-space'));
      const long = this.content.scrollHeight > this.collapsedHeight + 1;
      this.toggleAttribute('data-short', !long);
      this.button.hidden = !long;
      if (!long) {
        cancelAnimationFrame(this.animationFrame);
        clearTimeout(this.transitionTimer);
        this.expanded = false;
        this.removeAttribute('data-expanded');
        this.classList.remove('is-animating');
        this.style.height = '';
        this.button.setAttribute('aria-expanded', 'false');
        this.button.textContent = this.button.dataset.expandLabel;
        this.restoreFocus();
      } else if (!this.expanded) {
        this.style.height = `${this.collapsedHeight}px`;
        this.limitFocus();
      } else if (this.classList.contains('is-animating')) {
        this.style.height = `${this.content.scrollHeight + this.controlsSpace}px`;
      }
    }

    toggle() {
      cancelAnimationFrame(this.animationFrame);
      clearTimeout(this.transitionTimer);
      const height = this.getBoundingClientRect().height;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.style.height = `${height}px`;
      this.expanded = !this.expanded;
      this.toggleAttribute('data-expanded', this.expanded);
      this.button.setAttribute('aria-expanded', String(this.expanded));
      this.button.textContent = this.expanded ? this.button.dataset.collapseLabel : this.button.dataset.expandLabel;
      this.expanded ? this.restoreFocus() : this.limitFocus();

      if (!this.expanded) {
        window.scrollTo({
          top: Math.max(0, this.getBoundingClientRect().top + window.scrollY - 100),
          behavior: reducedMotion ? 'auto' : 'smooth'
        });
      }

      if (reducedMotion) {
        this.finishTransition();
        return;
      }
      this.classList.add('is-animating');
      void this.offsetHeight;
      this.animationFrame = requestAnimationFrame(() => {
        this.style.height = this.expanded ? `${this.content.scrollHeight + this.controlsSpace}px` : `${this.collapsedHeight}px`;
        this.transitionTimer = setTimeout(() => this.finishTransition(), 300);
      });
    }

    finishTransition() {
      clearTimeout(this.transitionTimer);
      this.classList.remove('is-animating');
      this.style.height = this.hasAttribute('data-short') ? '' : this.expanded ? 'auto' : `${this.collapsedHeight}px`;
    }

    limitFocus() {
      const bottom = this.content.getBoundingClientRect().top + this.collapsedHeight - this.controlsSpace;
      for (const element of this.content.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
        if (!this.focusable.has(element)) this.focusable.set(element, element.getAttribute('tabindex'));
        if (element.getBoundingClientRect().bottom > bottom) element.setAttribute('tabindex', '-1');
        else this.restoreTabIndex(element, this.focusable.get(element));
      }
    }

    restoreTabIndex(element, value) {
      value === null ? element.removeAttribute('tabindex') : element.setAttribute('tabindex', value);
    }

    restoreFocus() {
      if (!this.focusable) return;
      for (const [element, value] of this.focusable) this.restoreTabIndex(element, value);
      this.focusable.clear();
    }
  }

  customElements.define('collection-seo-text', CollectionSeoText);
  document.documentElement.classList.add('collection-seo-text-enabled');
})();
